import { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router';
import {
  useAppAuthenticatedInitializer,
  useAuthentication,
  useAuthSocketEvents,
  useGetAuthenticatedUser,
  useGetExternalMentions,
  useGetQuickReplies,
  useSocketEventsOrchestration,
  useStoreOrchestration,
} from '@connect-x-app/hybrid-state';
import { Flightrooms } from '@connect-x-app/web-feature-flightroom';
import { LicenseAgreement } from '@connect-x-app/web-feature-license';
import { useAppStateReconnect, useFirebaseConfigInitializer, useWindowFocus } from '@connect-x-app/web-state';
import { ModalOpener, OneButtonModal, Spinner } from '@connect-x-app/web-ui';
import { loadPdfViewer } from '@connect-x-app/web-utils';

import { DashboardLayout } from './dashboard-layout/DashboardLayout';

import './index.scss';

export const AppWeb = () => {
  document.title = 'Connect Web';
  loadPdfViewer();
  const { t } = useTranslation();
  const { setUser, user: authenticatedUser } = useAuthentication();
  const {
    data: user,
    isPending: isFetchingAuthentication,
    isSuccess: isAuthenticatedUserFetched,
  } = useGetAuthenticatedUser(false);

  useEffect(() => {
    if (user && isAuthenticatedUserFetched) {
      setUser(user);
    }
  }, [setUser, user, isAuthenticatedUserFetched]);

  if (isFetchingAuthentication && !authenticatedUser) {
    return <Spinner overlay>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  if (authenticatedUser && !authenticatedUser.hasAcceptedLicenseAgreement) {
    return (
      <Routes>
        <Route element={<LicenseAgreement />} path="*" />
      </Routes>
    );
  }

  if (authenticatedUser) {
    return (
      <AuthenticatedRoutes>
        <Route element={<DashboardLayout />} path="dashboard/*">
          <Route element={<Flightrooms />} path="flightrooms/:roomId?">
            <Route element={<Flightrooms />} path="channel/:channelId?" />
            <Route element={<Flightrooms />} path=":channelId?" />
          </Route>
          <Route element={<Navigate replace to="/dashboard/flightrooms" />} path="*" />
        </Route>
        <Route element={<Navigate replace to="/dashboard" />} path="*" />
      </AuthenticatedRoutes>
    );
  }

  return <Routes />;
};

const AuthenticatedRoutes = ({ children }: PropsWithChildren) => {
  useAppAuthenticatedInitializer();
  useAppStateReconnect();
  useGetQuickReplies();
  useGetExternalMentions();
  useWindowFocus();
  useSocketEventsOrchestration();
  useStoreOrchestration();
  useFirebaseConfigInitializer();

  const { t } = useTranslation();

  useAuthSocketEvents({
    onOpenAlert: closeAlert => {
      ModalOpener.instance.open({
        render: () => (
          <OneButtonModal
            confirmText={t('SHARED.BUTTONS.OK')}
            content={t('PERMISSIONS.PERMISSIONS_UPDATED.CONTENT')}
            onCancel={closeAlert}
            onConfirm={closeAlert}
            title={t('PERMISSIONS.PERMISSIONS_UPDATED.TITLE')}
          />
        ),
      });
    },
  });

  return <Routes>{children}</Routes>;
};
